/**
 * Handles attendee info for event tickets.
 *
 * When someone adds an event ticket to their cart, this opens a modal with one
 * form per ticket. Each form asks for the right info (email, gaming account,
 * t-shirt size, etc.) and can be pre-filled for logged-in customers. On submit,
 * each ticket becomes its own line item in the cart, carrying that attendee's info.
 * 
 * Flow: `handleProductForm`/`handleAlpineAdd` snapshot the clicked ticket into
 * `this.context` and open the modal; `render` clones one group (and its fields) from
 * `<template>`s per ticket quantity; `validate`/`collect` run on submit; `submitToCart`
 * builds the per-ticket line items and POSTs them to `/cart/add.js` in one request.
 *
 * Uses the theme's <modal-dialog> component (assets/global.js) for the popup.
 */
(function () {
  'use strict';

  // The fields to show for each type of event. Each field's `key` is used everywhere:
  // the form, the saved profile, and the cart. One field per group is `identity: true` —
  // that's the email used to check for duplicates.
  const FIELD_SETS = {
    standard: [{ key: 'email', type: 'email', required: true, identity: true }],
    riftbound: [
      { key: 'riftbound_email', type: 'email', required: true, identity: true },
      { key: 'riot_id', type: 'text', required: true }
    ],
    mtg: [{ key: 'wizards_email', type: 'email', required: true, identity: true }],
    mtg_high: [
      { key: 'wizards_email', type: 'email', required: true, identity: true },
      { key: 'wizards_display_name', type: 'text', required: true },
      { key: 'country', type: 'country', required: true }
    ]
  };

  const SHIRT_FIELD = { key: 'shirt_size', type: 'select', required: true };

  /** Add the shirt size field for shirt-eligible events to the form fieldset. */
  function fieldsForContext(ctx) {
    const fields = FIELD_SETS[ctx.fieldset] || FIELD_SETS.standard;
    return ctx.shirtIncluded ? fields.concat(SHIRT_FIELD) : fields;
  }

  // A loose email check just for the UI — Shopify does the real validation.
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Shopify escapes special characters in translated text (e.g. ' becomes &#39;),
  // so we decode it back here once, after it's loaded.
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  function decodeDeep(node) {
    if (typeof node === 'string') return decodeEntities(node);
    if (node && typeof node === 'object') {
      Object.keys(node).forEach((key) => { node[key] = decodeDeep(node[key]); });
    }
    return node;
  }

  const strings = decodeDeep(window.EventAttendeeStrings || {});

  // Whether the customer is logged in, and their saved info (if any).
  // profile is null for guests, or if they haven't saved anything yet.
  const customerData = window.EventAttendeeCustomer || { loggedIn: false, profile: null };

  // A random ID for each ticket, so Shopify keeps them as separate cart lines
  // instead of merging two tickets with the same info into one.
  function ticketUid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  const TICKET_TYPES = ['F2F Tour Event Tickets', 'Montréal Event Tickets', 'Toronto Event Tickets'];

  // The search index sometimes stores true/false as a real boolean, and sometimes
  // as the text 'true' — this checks for either.
  function isTrue(value) {
    return value === true || value === 'true';
  }

  function resolveFieldset(source) {
    if (!source || TICKET_TYPES.indexOf(source.product_type) === -1) return null;

    // Game_Type is a list metafield, so join it first rather than matching directly against the array.
    const rawGameType = source.General_Game_Type;
    const gameType = Array.isArray(rawGameType) ? rawGameType.join(',') : (rawGameType || '');
    if (gameType.indexOf('Riftbound') > -1) return 'riftbound';
    if (gameType.indexOf('Magic') > -1) {
      const rulesLevel = (source.Events_Rules_Enforcement_Level || '').toLowerCase();
      return rulesLevel === 'competitive rel' ? 'mtg_high' : 'mtg';
    }
    return 'standard';
  }

  const EventAttendee = {
    // Snapshot of the ticket currently being added — set by handleProductForm()
    context: null,
    // Cached references to the modal's DOM nodes; lazily populated the first time els() runs.
    _els: null,

    /** Find + cache the modal's DOM elements. Returns null if the modal isn't on this page. */
    els() {
      if (this._els) return this._els;
      const modal = document.getElementById('EventAttendeeModal');
      if (!modal) return null;
      this._els = {
        modal: modal,
        form: modal.querySelector('[data-attendee-form]'),
        groups: modal.querySelector('[data-attendee-groups]'),
        subtitle: modal.querySelector('[data-attendee-subtitle]'),
        cancel: modal.querySelector('[data-attendee-cancel]'),
        confirm: modal.querySelector('[data-attendee-confirm]'),
        error: modal.querySelector('[data-attendee-error]'),
        groupTemplate: document.getElementById('EventAttendeeFieldGroup'),
        fieldTemplate: document.getElementById('EventAttendeeField'),
        selectTemplate: document.getElementById('EventAttendeeSelectField'),
        countryTemplate: document.getElementById('EventAttendeeCountryOptions'),
        saveTemplate: document.getElementById('EventAttendeeSaveCheckbox')
      };
      this.bind();
      return this._els;
    },

    /** Wire up the modal's own controls. Runs once, right after els() first builds the cache. */
    bind() {
      this._els.form.addEventListener('submit', this.onConfirm.bind(this));
      this._els.cancel.addEventListener('click', this.close.bind(this));
    },

    /**
     * Saves the ticket info, builds the forms, and opens the modal.
     * Returns false if there's no modal on this page, so the caller can fall
     * back to a normal add-to-cart.
     */
    open(context, opener) {
      const els = this.els();
      if (!els) return false; // no modal on this page -> fall back to normal add-to-cart

      this.context = context;
      this.render();
      els.modal.show(opener);
      return true;
    },

    /**
     * Called by product-form.js when an event-ticket form is submitted.
     * Returns true if it opened the modal and took over the submit.
     */
    handleProductForm(productForm) {
      // Only event-ticket buttons carry this attribute (doubles as our eligibility check)
      const button = productForm.querySelector('[data-event-ticket="true"]');
      if (!button) return false;

      const form = productForm.querySelector('form');
      const qtyInput = form && form.elements ? form.elements.quantity : null;
      let qty = parseInt((qtyInput && qtyInput.value) || '1', 10);
      if (isNaN(qty) || qty < 1) qty = 1; // fall back safely if the input is missing/invalid

      // Snapshot everything render() and submitToCart() will need, before the modal takes over.
      return this.open({
        productForm: productForm,
        fieldset: button.getAttribute('data-event-fieldset') || 'standard',
        shirtIncluded: button.getAttribute('data-event-shirt') === 'true',
        quantity: qty,
        variantId: form && form.elements && form.elements.id ? form.elements.id.value : null
      }, button);
    },

    /**
     * Called from the Alpine collection-grid "add to cart" buttons.
     * `source` is the product data from the search index.
     * Returns false if this isn't a ticket (or there's no modal), so the
     * caller can fall back to its normal add-to-cart.
     */
    handleAlpineAdd(source, variantId, quantity, opener) {
      const fieldset = resolveFieldset(source);
      if (!fieldset) return false;

      let qty = parseInt(quantity, 10);
      if (isNaN(qty) || qty < 1) qty = 1;

      return this.open({
        fieldset: fieldset,
        shirtIncluded: isTrue(source.Events_Shirt_Included),
        quantity: qty,
        variantId: variantId
      }, opener || document.activeElement);
    },

    /** (Re)build the modal's forms for the current context: one group per ticket. */
    render() {
      const els = this._els;
      const ctx = this.context;
      const fields = fieldsForContext(ctx);

      // Reset UI state in case this modal was left mid-error/mid-submit from a previous open.
      this.clearError();
      this.setBusy(false);

      els.groups.textContent = '';
      for (let i = 0; i < ctx.quantity; i++) {
        els.groups.appendChild(this.buildGroup(i, ctx.quantity, fields));
      }

      // Singular vs. plural copy depending on how many tickets are being added.
      if (els.subtitle) {
        const sub = strings.modal_subtitle || {};
        els.subtitle.textContent = ctx.quantity > 1 ? (sub.other || '') : (sub.one || '');
      }
    },

    /** Clone one attendee group from the <template>, label it, and fill in its fields. */
    buildGroup(index, total, fields) {
      const els = this._els;
      const group = els.groupTemplate.content.firstElementChild.cloneNode(true);
      const legend = group.querySelector('.event-attendee-group__legend');

      if (legend) {
        // Only number the groups (Attendee 1, 2, ...) when there's more than one to tell apart.
        if (total > 1) {
          legend.textContent = (strings.attendee_heading || 'Attendee') + ' ' + (index + 1);
        } else {
          legend.remove();
        }
      }

      // Only the first form (the purchaser) can be pre-filled or saved to an
      // account — the other attendees are different people.
      const isPurchaser = index === 0;
      const profile = (isPurchaser && customerData.loggedIn && customerData.profile) || null;

      const wrap = group.querySelector('.event-attendee-group__fields');
      // Clone the field template once per field this event's field-set needs.
      fields.forEach((field) => {
        const isDropdown = field.type === 'select' || field.type === 'country';
        const template = isDropdown ? els.selectTemplate : els.fieldTemplate;
        const node = template.content.firstElementChild.cloneNode(true);
        const input = node.querySelector('.event-attendee-field__input');
        const label = node.querySelector('.event-attendee-field__label');
        const id = 'attendee-' + index + '-' + field.key;
        const labelText = (strings.fields && strings.fields[field.key]) || field.key;

        input.name = field.key;
        input.id = id;
        input.setAttribute('data-field-key', field.key); // read back later by collect()
        if (field.required) input.required = true;

        if (field.type === 'select') {
          this.populateOptions(input, strings.shirt_size_groups || []);
        } else if (field.type === 'country') {
          this.populateCountryOptions(input);
        } else {
          input.type = field.type;
          // Let browsers autofill email fields; don't offer autofill guesses for IDs/names.
          input.setAttribute('autocomplete', field.type === 'email' ? 'email' : 'off');
        }
        if (profile && profile[field.key]) input.value = profile[field.key]; // pre-fill by field identity

        label.setAttribute('for', id);
        label.textContent = field.required ? labelText + ' *' : labelText; // flag required fields

        wrap.appendChild(node);
      });

      // Only the logged-in purchaser sees a "save to my account" checkbox.
      // It's always unchecked by default — saving is opt-in every time.
      if (isPurchaser && customerData.loggedIn && els.saveTemplate) {
        const saveNode = els.saveTemplate.content.firstElementChild.cloneNode(true);
        const saveLabel = saveNode.querySelector('span');
        if (saveLabel) saveLabel.textContent = strings.save_to_profile || '';
        group.appendChild(saveNode);
      }

      return group;
    },

    /** Fills a <select> with a blank placeholder plus one <optgroup> per size group. */
    populateOptions(select, groups) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = strings.select_placeholder || '';
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      groups.forEach((group) => {
        const optgroup = document.createElement('optgroup');
        optgroup.label = group.label;
        group.sizes.forEach((size) => {
          const option = document.createElement('option');
          // Spell out sex + size, so eg: "Mens - XS", not just "XS"
          const text = group.label + ' - ' + size;
          option.value = text;
          option.textContent = text;
          optgroup.appendChild(option);
        });
        select.appendChild(optgroup);
      });
    },

    // Fills a <select> with Shopify's own country list, using all_country_option_tags
    populateCountryOptions(select) {
      const template = this._els.countryTemplate;
      if (template) select.appendChild(template.content.cloneNode(true));
    },

    /** Flags one input invalid with a message, or clears it when message is falsy. */
    setFieldError(input, message) {
      const error = input.parentElement.querySelector('.event-attendee-field__error');
      if (message) {
        input.setAttribute('aria-invalid', 'true');
        if (error) { error.textContent = message; error.hidden = false; }
      } else {
        input.removeAttribute('aria-invalid');
        if (error) { error.hidden = true; error.textContent = ''; }
      }
    },

    /** Check every visible field for required/format errors; focuses the first invalid one. */
    validate() {
      const errs = strings.errors || {};
      const inputs = this._els.groups.querySelectorAll('.event-attendee-field__input');
      let firstInvalid = null;

      inputs.forEach((input) => {
        const value = input.value.trim();
        let message = '';

        // Required check first; format check only applies once a value is actually present.
        if (input.required && !value) {
          message = errs.required || 'This field is required.';
        } else if (input.type === 'email' && value && !EMAIL_REGEX.test(value)) {
          message = errs.email || 'Enter a valid email address.';
        }

        this.setFieldError(input, message);
        if (message && !firstInvalid) firstInvalid = input; // remember the first one, for focus below
      });

      if (firstInvalid) firstInvalid.focus();
      return !firstInvalid;
    },

    /**
     * Checks for a duplicate attendee email on this ticket — first among the
     * forms being submitted, then against tickets already in the cart. Matches
     * the same rule checkout uses, so nothing slips through later. Resolves to
     * true if it's safe to submit.
     */
    checkDuplicates(fields, attendees) {
      const identity = fields.find((field) => field.identity);
      if (!identity) return Promise.resolve(true); // no identity field defined — nothing to check

      const message = (strings.errors && strings.errors.duplicate_email) || 'This ticket has already been added for that email address.';
      const groups = this._els.groups.querySelectorAll('.event-attendee-group');
      const values = attendees.map((attendee) => (attendee[identity.key] || '').toLowerCase());

      const flag = (index) => {
        const input = groups[index].querySelector('[data-field-key="' + identity.key + '"]');
        this.setFieldError(input, message);
        input.focus();
      };

      // Check for duplicates within this submission first — cheap, no network needed.
      const seen = new Set();
      for (let i = 0; i < values.length; i++) {
        if (values[i] && seen.has(values[i])) { flag(i); return Promise.resolve(false); }
        seen.add(values[i]);
      }

      // Then against attendees already in the cart for this same ticket variant.
      return fetch(routes.cart_url + '.js')
        .then((response) => response.json())
        .then((cart) => {
          const existing = new Set();
          (cart.items || []).forEach((item) => {
            const email = item.variant_id == this.context.variantId && item.properties
              ? item.properties._receiver_email
              : null;
            if (email) existing.add(email);
          });

          const dupIndex = values.findIndex((value) => value && existing.has(value));
          if (dupIndex === -1) return true;

          flag(dupIndex);
          return false;
        })
        // If this check fails (e.g. no internet), don't block the submit —
        // checkout will still catch duplicates as a backup.
        .catch(() => true);
    },

    isSaveToProfileChecked() {
      const checkbox = this._els.groups.querySelector('[data-save-checkbox]');
      return !!(checkbox && checkbox.checked);
    },

    /** Collect one attendee object per ticket group, keyed by field key. */
    collect() {
      const attendees = [];
      this._els.groups.querySelectorAll('.event-attendee-group').forEach((group) => {
        const data = {};
        group.querySelectorAll('.event-attendee-field__input').forEach((input) => {
          data[input.getAttribute('data-field-key')] = input.value.trim();
        });
        attendees.push(data);
      });
      return attendees;
    },

    onConfirm(evt) {
      evt.preventDefault(); // stop the native submit (would otherwise reload the page)
      if (this._submitting) return; // ignore a second click while a request is already in flight
      if (!this.validate()) return;

      const fields = fieldsForContext(this.context);
      const attendees = this.collect();

      // Show the loading state now, since checking for duplicates also takes
      // a moment (not just the final add-to-cart).
      this.setBusy(true);
      this.checkDuplicates(fields, attendees).then((ok) => {
        if (ok) {
          this.submitToCart(attendees);
        } else {
          this.setBusy(false);
        }
      });
    },

    /**
     * Adds one cart line per ticket, each with the attendee's info attached,
     * then refreshes the cart.
     */
    submitToCart(attendees) {
      const ctx = this.context;
      const fields = fieldsForContext(ctx);
      const cartDrawer = document.querySelector('cart-drawer');
      const saveToProfile = this.isSaveToProfileChecked();
      const identity = fields.find((field) => field.identity);

      // Build one cart line item per attendee, each added in the same request.
      const items = attendees.map((attendee, index) => {
        const properties = {};
        // Visible, human-readable props (shown in cart / checkout / email / admin).
        fields.forEach((field) => {
          const label = (strings.fields && strings.fields[field.key]) || field.key;
          properties[label] = attendee[field.key] || '';
        });
        // Hidden props (leading underscore => not shown to the customer, still readable via API).
        properties._event_type = ctx.fieldset; // backend discriminator
        properties._ticket_uid = ticketUid(); // forces one line item per ticket (see ticketUid above)
        properties._attendee_data = JSON.stringify(Object.assign({ type: ctx.fieldset }, attendee)); // backend blob
        properties._save_to_profile = (index === 0 && saveToProfile) ? 'true' : 'false';
        // The attendee's email, cleaned up — checkout's duplicate check reads
        // this exact value, so it must match what checkDuplicates() compares.
        properties._receiver_email = identity ? (attendee[identity.key] || '').trim().toLowerCase() : '';
        return { id: ctx.variantId, quantity: 1, properties: properties };
      });

      const body = { items: items };
      if (cartDrawer) {
        // Same setup product-form.js uses for a normal add: which sections
        // to refresh, and what to focus once the drawer closes.
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cartDrawer.setActiveElement(document.activeElement);
      }

      this.setBusy(true);
      this.clearError(); // clear any error left over from a previous failed attempt

      return fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            // Same reload-on-limit check as product-form.js's normal add path.
            if (response.items && response.items.length === window.limit_cart) window.location.reload();

            // If this fails, nothing was added (it's all-or-nothing). Keep
            // the modal open and show the error so they can fix it and retry.
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'event-attendee',
              productVariantId: ctx.variantId,
              errors: response.errors || response.description,
              message: response.message
            });
            this.showError(response.message || response.description);
            return;
          }

          this.close();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'event-attendee', productVariantId: ctx.variantId });

          if (cartDrawer) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(response);
          } else {
            // The cart notification popup only handles one item at a time, so
            // send the customer to the cart page to see all their tickets.
            window.location = routes.cart_url;
          }
        })
        .catch((error) => {
          // Network failure (offline, etc.) rather than a Shopify-side rejection.
          console.error('[EventAttendee]', error);
          this.showError(window.cartStrings && window.cartStrings.error);
        })
        .finally(() => {
          // Always clear the loading state, whether we succeeded, errored, or threw.
          this.setBusy(false);

          // Same as product-form.js: once the cart hits its item limit,
          // disable every add-to-cart button on the page.
          const cartItemCounter = document.getElementById('bb-wbhcartitem');
          if (cartItemCounter && cartItemCounter.getAttribute('data-unique') == window.limit_cart) {
            document.querySelectorAll('.product-form__submit').forEach((button) => { button.disabled = true; });
          }
        });
    },

    /** Toggle a simple loading state on the modal's buttons while a request is in flight. */
    setBusy(isBusy) {
      this._submitting = isBusy;
      const els = this._els;
      if (!els) return;
      if (els.confirm) {
        els.confirm.disabled = isBusy;
        els.confirm.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }
      if (els.cancel) els.cancel.disabled = isBusy;
    },

    // A general error message (like "sold out") shown below the form — not
    // the same as the per-field errors from validate().
    showError(message) {
      const els = this._els;
      if (!els || !els.error) return;
      els.error.textContent = message || '';
      els.error.hidden = !message;
    },

    clearError() {
      const els = this._els;
      if (els && els.error) {
        els.error.hidden = true;
        els.error.textContent = '';
      }
    },

    close() {
      const els = this.els();
      if (els) els.modal.hide();
    }
  };

  window.EventAttendee = EventAttendee;
})();
